import React, { useState, ChangeEvent, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Box,
  Typography,
  SelectChangeEvent,
  FormHelperText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ManualCreditGrantAction } from "../../../Redux/Actions/PaymentAction";

export interface ManualCreditGrantForm {
  entity_type: "USER" | "ORGANIZATION";
  entity_id: number;
  credits_to_grant: number;
  credit_reason: string;
  invoice_id: string;
  amount_paid: number;
  currency: string;
  reference_type: "BANK_TRANSFER" | "CHEQUE" | "OTHER";
  external_reference: string;
  notes?: string;
}

interface ManualCreditGrantProps {
  open: boolean;
  onClose: () => void;
  entityType?: "USER" | "ORGANIZATION";
  entityId?: number;
  entityName?: string;
  entityEmail?: string;
}

const initialFormState: ManualCreditGrantForm = {
  entity_type: "USER",
  entity_id: 0,
  credits_to_grant: 0,
  credit_reason: "",
  invoice_id: "",
  amount_paid: 0,
  currency: "INR",
  reference_type: "BANK_TRANSFER",
  external_reference: "",
  notes: "",
};

// Kept in sync with the backend's MANUAL_PAYMENT_INVOICE_ID_PATTERN
// (app/schemas/schemas.py) — Razorpay invoice ids are "inv_" + 14
// alphanumeric characters.
const INVOICE_ID_PATTERN = /^inv_[A-Za-z0-9]{14}$/;

const manualCreditGrantSchema = yup.object().shape({
  entity_type: yup
    .string()
    .oneOf(["USER", "ORGANIZATION"], "Invalid entity type")
    .required("Entity type is required"),
  entity_id: yup
    .number()
    .min(1, "Entity ID must be at least 1")
    .required("Entity ID is required"),
  credits_to_grant: yup
    .number()
    .min(1, "Credits must be at least 1")
    .required("Credits is required"),
  credit_reason: yup
    .string()
    .trim()
    .required("Reason is required")
    .max(255, "Reason must be at most 255 characters"),
  invoice_id: yup
    .string()
    .matches(
      INVOICE_ID_PATTERN,
      "Invoice ID must be 'inv_' followed by 14 alphanumeric characters"
    )
    .required("Invoice ID is required"),
  amount_paid: yup
    .number()
    .min(0.01, "Amount must be greater than 0")
    .required("Amount paid is required"),
  currency: yup
    .string()
    .oneOf(
      ["INR", "USD", "GBP", "EUR", "AUD", "CAD", "SGD", "AED"],
      "Invalid currency"
    )
    .required("Currency is required"),
  reference_type: yup
    .string()
    .oneOf(
      ["BANK_TRANSFER", "CHEQUE", "OTHER"],
      "Invalid reference type"
    )
    .required("Reference type is required"),
  external_reference: yup.string().required("External reference is required"),
  notes: yup.string().nullable(),
});

const currencyOptions = [
  "INR",
  "USD",
  "GBP",
  "EUR",
  "AUD",
  "CAD",
  "SGD",
  "AED",
];

const referenceHint: Record<ManualCreditGrantForm["reference_type"], string> = {
  BANK_TRANSFER: "Enter the bank UTR — must be unique across all orgs.",
  CHEQUE:
    "Enter the bank's clearing/realization number if available. If that isn't " +
    "available yet, use a composite string: cheque number + bank name + last 4 " +
    "account digits (e.g. CHQ-1234-HDFC-5678). Must be unique across all orgs — " +
    "a cheque number alone isn't unique enough.",
  OTHER: "Free-text deal reference — only needs to be unique within this org.",
};

interface ManualGrantSuccess {
  entityName: string;
  creditsGranted: number | null;
}

const ManualCreditGrant: React.FC<ManualCreditGrantProps> = ({
  open,
  onClose,
  entityType,
  entityId,
  entityName,
  entityEmail,
}) => {
  const dispatch = useDispatch<any>();

  const [formData, setFormData] = useState<ManualCreditGrantForm>(() => ({
    ...initialFormState,
    entity_type: entityType || "USER",
    entity_id: entityId || 0,
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [successData, setSuccessData] = useState<ManualGrantSuccess | null>(
    null
  );

  const GrantResponse = useSelector(
    (state: any) => state.ManualCreditGrantData
  );

  useEffect(() => {
    if (!submitted || GrantResponse?.loading) return;

    if (GrantResponse?.status) {
      const grantData = GrantResponse?.data || {};
      setSuccessData({
        entityName: entityName || `${formData.entity_type} #${formData.entity_id}`,
        creditsGranted: grantData.credits_granted ?? null,
      });
      setSubmitted(false);
    } else if (GrantResponse?.data) {
      const message = GrantResponse?.data?.message || "";
      const lowered = message.toLowerCase();
      if (lowered.includes("invoice")) {
        setErrors((prev) => ({ ...prev, invoice_id: message }));
      } else if (lowered.includes("reference") || lowered.includes("duplicate") || lowered.includes("already been used")) {
        setErrors((prev) => ({ ...prev, external_reference: message }));
      } else if (lowered.includes("credit_reason") || lowered.includes("reason")) {
        setErrors((prev) => ({ ...prev, credit_reason: message }));
      } else {
        toast.error(message);
      }
      setSubmitted(false);
    }
  }, [GrantResponse, submitted, entityName, formData.entity_type, formData.entity_id]);

  useEffect(() => {
    if (open) {
      return;
    }
    setFormData({
      ...initialFormState,
      entity_type: entityType || "USER",
      entity_id: entityId || 0,
    });
    setErrors({});
    setSubmitted(false);
    setSuccessData(null);
  }, [open, entityType, entityId]);

  const handleDone = () => {
    setSuccessData(null);
    onClose();
  };

  const handleChange = (
    e:
      | ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
      | SelectChangeEvent<string | number>
  ) => {
    const { name, value } = e.target;
    if (!name) return;
    const numericFields = ["entity_id", "credits_to_grant", "amount_paid"];
    setFormData((prev) => {
      if (name === "entity_type" && value !== prev.entity_type) {
        return {
          ...prev,
          entity_type: value as "USER" | "ORGANIZATION",
          entity_id: 0,
        };
      }
      return {
        ...prev,
        [name]: numericFields.includes(name) ? Number(value) : value,
      };
    });
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = () => {
    if (submitted) return;
    manualCreditGrantSchema
      .validate(formData, { abortEarly: false })
      .then(() => {
        setErrors({});
        setSubmitted(true);
        dispatch(ManualCreditGrantAction({ ...formData }));
      })
      .catch((validationError: yup.ValidationError) => {
        const errorMessages: Record<string, string> = {};
        validationError.inner.forEach((error) => {
          if (error.path) {
            errorMessages[error.path] = error.message;
          }
        });
        setErrors(errorMessages);
      });
  };

  if (successData) {
    return (
      <Dialog open={open} onClose={handleDone} maxWidth="sm" fullWidth>
        <DialogTitle>Credits Issued</DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              py: 2,
            }}
          >
            <CheckCircleIcon color="success" sx={{ fontSize: 56 }} />
            <Typography variant="h6" fontWeight={600}>
              Credits issued successfully
            </Typography>
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography color="text.secondary">Entity</Typography>
                <Typography fontWeight={600}>{successData.entityName}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography color="text.secondary">Reason</Typography>
                <Typography fontWeight={600}>{formData.credit_reason}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography color="text.secondary">Credits granted</Typography>
                <Typography fontWeight={600}>
                  {successData.creditsGranted ?? "—"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDone} variant="contained" color="primary">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Grant Credits Manually</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {(entityName || entityEmail) && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: "grey.100",
                border: "1px solid",
                borderColor: "grey.300",
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                {entityName || `${entityType} #${entityId}`}
              </Typography>
              {entityEmail && (
                <Typography variant="body2" color="text.secondary">
                  {entityEmail}
                </Typography>
              )}
            </Box>
          )}

          <label>Entity Type</label>
          <FormControl
            fullWidth
            variant="outlined"
            error={Boolean(errors.entity_type)}
          >
            <Select
              name="entity_type"
              value={formData.entity_type}
              onChange={handleChange}
              disabled={Boolean(entityType)}
            >
              <MenuItem value="USER">User</MenuItem>
              <MenuItem value="ORGANIZATION">Organization</MenuItem>
            </Select>
            {errors.entity_type && (
              <Box
                component="span"
                sx={{ color: "red", fontSize: "0.75rem" }}
              >
                {errors.entity_type}
              </Box>
            )}
          </FormControl>

          <label>Entity ID</label>
          <TextField
            name="entity_id"
            type="number"
            placeholder="Enter Entity ID"
            value={formData.entity_id || ""}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            disabled={Boolean(entityType)}
            error={Boolean(errors.entity_id)}
            helperText={errors.entity_id}
            InputProps={{
              inputProps: { min: 1 },
            }}
          />

          <label>Credits to Grant</label>
          <TextField
            name="credits_to_grant"
            type="number"
            placeholder="Enter number of credits"
            value={formData.credits_to_grant || ""}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            error={Boolean(errors.credits_to_grant)}
            helperText={
              errors.credits_to_grant ||
              "Number of credits to deposit into the entity's wallet"
            }
            InputProps={{
              inputProps: { min: 1, step: 1 },
            }}
          />

        <label>Reason</label>
        <TextField
          name="credit_reason"
          placeholder="e.g. Goodwill credits for onboarding delay"
          value={formData.credit_reason || ""}
          onChange={handleChange}
          variant="outlined"
          fullWidth
          error={Boolean(errors.credit_reason)}
          helperText={
            errors.credit_reason ||
            "Recorded on the transaction and in the credit history"
          }
          inputProps={{ maxLength: 255 }}
        />

        <label>Razorpay Invoice ID</label>
          <TextField
            name="invoice_id"
            placeholder="inv_XXXXXXXXXXXXXX"
            value={formData.invoice_id}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            error={Boolean(errors.invoice_id)}
            helperText={
              errors.invoice_id ||
              "Create the invoice in Razorpay first, then paste its id here."
            }
          />

          <label>Amount Paid</label>
          <TextField
            name="amount_paid"
            type="number"
            placeholder="Enter Amount Paid"
            value={formData.amount_paid || ""}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            error={Boolean(errors.amount_paid)}
            helperText={errors.amount_paid}
            InputProps={{
              inputProps: { min: 0.01, step: 0.01 },
            }}
          />

          <label>Currency</label>
          <FormControl
            fullWidth
            variant="outlined"
            error={Boolean(errors.currency)}
          >
            <Select
              name="currency"
              value={formData.currency}
              onChange={handleChange}
            >
              {currencyOptions.map((currency) => (
                <MenuItem key={currency} value={currency}>
                  {currency}
                </MenuItem>
              ))}
            </Select>
            {errors.currency && (
              <Box
                component="span"
                sx={{ color: "red", fontSize: "0.75rem" }}
              >
                {errors.currency}
              </Box>
            )}
          </FormControl>

          <label>Reference Type</label>
          <FormControl
            fullWidth
            variant="outlined"
            error={Boolean(errors.reference_type)}
          >
            <Select
              name="reference_type"
              value={formData.reference_type}
              onChange={handleChange}
            >
              <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
              <MenuItem value="CHEQUE">Cheque</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </Select>
            {errors.reference_type && (
              <Box
                component="span"
                sx={{ color: "red", fontSize: "0.75rem" }}
              >
                {errors.reference_type}
              </Box>
            )}
          </FormControl>

          <label>External Reference</label>
          <TextField
            name="external_reference"
            placeholder="Enter External Reference"
            value={formData.external_reference}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            error={Boolean(errors.external_reference)}
            helperText={errors.external_reference}
          />
          {!errors.external_reference && (
            <FormHelperText sx={{ mt: -1.5 }}>
              {referenceHint[formData.reference_type]}
            </FormHelperText>
          )}

          <label>Notes</label>
          <TextField
            name="notes"
            placeholder="Enter Notes (optional)"
            value={formData.notes || ""}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={submitted}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={submitted}
        >
          {submitted ? "Granting…" : "Grant Credits"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualCreditGrant;
